import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated';

import { ModernPalette } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { getStickyNoteColor } from '@/constants/sticky-note-colors';
import type { AISuggestion } from '@/types/ai-suggestions';

type AddTaskStatus = 'idle' | 'loading' | 'success' | 'error';

type SuggestionStackCardProps = {
  suggestion: AISuggestion;
  onAddToCalendar: (suggestion: AISuggestion) => void;
  onAddToTodo: (suggestion: AISuggestion) => Promise<boolean>;
  onDismiss: (suggestion: AISuggestion) => void;
};

/**
 * SuggestionStackCard - Individual card rendered inside the FlingStack.
 * Sticky-note style with pastel background, drop shadow, and add button.
 */
export function SuggestionStackCard({
  suggestion,
  onAddToCalendar,
  onAddToTodo,
  onDismiss,
}: SuggestionStackCardProps) {
  const backgroundColor = getStickyNoteColor(suggestion.id);
  const [addTaskStatus, setAddTaskStatus] = useState<AddTaskStatus>('idle');
  const [isExpanded, setIsExpanded] = useState(false);
  const hasMore = (suggestion.actions?.length ?? 0) > 0;

  const handleAddToTodo = async () => {
    if (addTaskStatus !== 'idle') return;
    setAddTaskStatus('loading');
    const success = await onAddToTodo(suggestion);
    setAddTaskStatus(success ? 'success' : 'error');
  };

  const isAddDisabled = addTaskStatus !== 'idle';

  return (
    <View style={[styles.card, { backgroundColor }]}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="sparkles" size={20} color="#555" />
        <Text
          style={[styles.title, { fontFamily: getBodyFont() }]}
          numberOfLines={2}
        >
          {suggestion.title}
        </Text>
        <TouchableOpacity
          onPress={() => onDismiss(suggestion)}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          style={styles.dismissButton}
          accessibilityLabel="Dismiss suggestion"
        >
          <Ionicons name="close" size={20} color="#999" />
        </TouchableOpacity>
      </View>

      {/* Description */}
      <Text
        style={[styles.description, { fontFamily: getBodyFont() }]}
        numberOfLines={isExpanded ? undefined : 3}
      >
        {suggestion.description}
      </Text>

      {/* Action bullets — only when expanded */}
      {isExpanded && (suggestion.actions?.length ?? 0) > 0 && (
        <View style={styles.actionsList}>
          {suggestion.actions!.map((action, idx) => (
            <View key={`${action}-${idx}`} style={styles.actionRow}>
              <Text style={[styles.actionBullet, { fontFamily: getBodyFont() }]}>•</Text>
              <Text style={[styles.actionText, { fontFamily: getBodyFont() }]}>{action}</Text>
            </View>
          ))}
        </View>
      )}

      {/* See more / See less toggle */}
      {hasMore && (
        <TouchableOpacity
          onPress={() => setIsExpanded((prev) => !prev)}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel={isExpanded ? 'See less' : 'See more'}
        >
          <Text style={[styles.seeMoreText, { fontFamily: getBodyFont() }]}>
            {isExpanded ? 'See less' : 'See more'}
          </Text>
        </TouchableOpacity>
      )}

      {/* Footer: action buttons */}
      <View style={styles.footer}>
        <AddTaskButton status={addTaskStatus} onPress={handleAddToTodo} disabled={isAddDisabled} />
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => onAddToCalendar(suggestion)}
          activeOpacity={0.8}
          accessibilityLabel="Add to calendar"
        >
          <Ionicons name="calendar-outline" size={18} color={ModernPalette.textInverse} />
          <Text style={[styles.actionButtonText, { fontFamily: getBodyFont() }]}>Add Event</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

type AddTaskButtonProps = {
  status: AddTaskStatus;
  onPress: () => void;
  disabled: boolean;
};

function AddTaskButton({ status, onPress, disabled }: AddTaskButtonProps) {
  const getButtonStyle = () => {
    switch (status) {
      case 'success': return [styles.actionButton, styles.actionButtonSuccess];
      case 'error': return [styles.actionButton, styles.actionButtonError];
      default: return styles.actionButton;
    }
  };

  return (
    <TouchableOpacity
      style={getButtonStyle()}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={disabled}
      accessibilityLabel="Add to todo list"
    >
      {status === 'loading' && (
        <ActivityIndicator size="small" color={ModernPalette.textInverse} />
      )}
      {status === 'success' && (
        <Animated.View entering={ZoomIn.duration(200)}>
          <Ionicons name="checkmark" size={18} color={ModernPalette.textInverse} />
        </Animated.View>
      )}
      {status === 'error' && (
        <Animated.View entering={FadeIn.duration(200)}>
          <Ionicons name="alert-circle" size={18} color={ModernPalette.textInverse} />
        </Animated.View>
      )}
      {status === 'idle' && (
        <Ionicons name="add" size={18} color={ModernPalette.textInverse} />
      )}
      <Text style={[styles.actionButtonText, { fontFamily: getBodyFont() }]}>
        {status === 'success' ? 'Added!' : status === 'error' ? 'Failed' : 'Add Task'}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: BorderRadius.lg,
    padding: Spacing.xl,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
    gap: Spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    flex: 1,
    color: '#333',
  },
  dismissButton: {
    padding: Spacing.xxs,
  },
  description: {
    fontSize: 15,
    lineHeight: 22,
    color: '#555',
  },
  actionsList: {
    gap: Spacing.xxs,
    marginTop: Spacing.xs,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.xs,
  },
  actionBullet: {
    fontSize: 14,
    lineHeight: 20,
    color: '#555',
    width: 10,
  },
  actionText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#555',
    flex: 1,
  },
  seeMoreText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    textDecorationLine: 'underline',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: Spacing.xs,
    gap: Spacing.sm,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: Spacing.md,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#333',
  },
  actionButtonSuccess: {
    backgroundColor: '#2E7D32',
  },
  actionButtonError: {
    backgroundColor: '#C62828',
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: ModernPalette.textInverse,
  },
});
